import { Dumbbell, Clock, Repeat, Weight } from "lucide-react";
import { youTubeThumbUrl } from "@/lib/youtube";

export interface ExerciseCardProps {
  name: string;
  /** Detalhes prescritos formatados (ex: "4 séries × 8-10 reps"). */
  detail?: string | null;
  /** Carga prescrita formatada (ex: "60 kg"). */
  loadLabel?: string | null;
  /** Descanso entre séries em segundos. */
  restSeconds?: number | null;
  /** URL pública do GIF/imagem (já completa). */
  gifUrl?: string | null;
  /** URL de vídeo (usado como fallback de mídia). */
  videoUrl?: string | null;
  /** Imagem estática como fallback do gif. */
  imageUrl?: string | null;
}

function formatRest(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}min`;
}

/**
 * Card de exercício do portal — layout horizontal, mídia à esquerda
 * (96×96 rounded-2xl) + info à direita (nome + chips de prescrição). Portado de
 * `frontend/components/portal/ExerciseCard.tsx` (Next) com classes do tema novo.
 */
export function ExerciseCard({
  name,
  detail,
  loadLabel,
  restSeconds,
  gifUrl,
  videoUrl,
  imageUrl,
}: ExerciseCardProps) {
  // Link do YouTube vira <img> do thumbnail (não renderiza em <video>); arquivo
  // de vídeo (upload legado) cai no <video> abaixo. Ver `@/lib/youtube`.
  const mediaSrc = gifUrl ?? imageUrl ?? youTubeThumbUrl(videoUrl) ?? null;
  const restLabel = formatRest(restSeconds);

  return (
    <div className="rounded-2xl border border-border-subtle bg-surface p-3 transition-colors hover:border-border-strong">
      <div className="flex gap-3">
        {/* Mídia 96×96 */}
        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-border-subtle bg-elevated">
          {mediaSrc ? (
            <img
              src={mediaSrc}
              alt={name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : videoUrl ? (
            <video
              src={videoUrl}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
              aria-label={name}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Dumbbell size={28} className="text-faint opacity-60" aria-hidden />
            </div>
          )}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"
          />
        </div>

        {/* Conteúdo */}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold leading-tight text-ink">{name}</h3>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {detail ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-elevated px-2 py-0.5 text-[11px] font-medium text-muted">
                <Repeat size={10} aria-hidden />
                {detail}
              </span>
            ) : null}
            {loadLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-accent-soft/35 bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent-soft">
                <Weight size={10} aria-hidden />
                {loadLabel}
              </span>
            ) : null}
            {restLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-elevated px-2 py-0.5 text-[11px] font-medium text-muted">
                <Clock size={10} aria-hidden />
                {restLabel}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
